import {
    CartIcon,
    DuplicateIcon,
    EditIcon,
    EllipsisIcon,
    EmailIcon,
    EyeIcon,
    LockIcon,
    ShoppingBagIcon,
    TrashIcon,
    WarningIcon,
    WifiIcon,
} from "@xola/icons";
import { Badge, FadeIn, flash, PopoverList, Tooltip } from "@xola/ui-kit";
import { getDraftReasons, isDraft } from "common";
import React, { ReactElement, useRef, useState } from "react";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import { ButtonType, DelegateRole, EmailTemplate, Experience, ExperienceStatus, UserRole } from "types";
import { NoTranslate } from "../Common/NoTranslate";
import { ProductImage } from "../Common/ProductImage";
import { PromptModal } from "../Common/PromptModal";
import { SecondaryButton } from "../Common/SecondaryButton";
import { PreviewMessageTemplate } from "../Customers/Templates/PreviewMessageTemplate";
import { DuplicateExperienceModal } from "./DuplicateExperience/DuplicateExperienceModal";
import { DuplicateSuccessModal } from "./DuplicateExperience/DuplicateSuccessModal";
import { api } from "@/api/api";
import { useSeller, useUser } from "@/hooks/useAccount";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useVisibility } from "@/hooks/useVisibility";
import { getButtonUrl, isX2CheckoutSupported, openCheckoutIframe } from "@/models/ButtonCode";
import { DuplicateSuccessData } from "@/models/DuplicateExperience";
import { hasPrivateOption, sanitizeExperiencePayload } from "@/models/Experience";
import { BookabilityStatus, useBookabilityStatus } from "@/models/ExperienceBookability";
import { isXolaAdmin } from "@/models/User";

export const ExperienceTile = ({ experience }: { experience: Experience }) => {
    const excerptRef = useRef<HTMLDivElement | null>(null);
    const element = excerptRef.current?.childNodes?.[0] as HTMLElement | null;
    const isTruncated = element ? element.scrollHeight > element.clientHeight : false;

    const isVirtual = experience.virtualMeeting?.enabled ?? false;
    const history = useHistory();
    const isHidden = !experience.visible;

    const draftReasons = getDraftReasons(experience);
    const isDraftExperience = isDraft(experience) || draftReasons.length > 0;

    const bookabilityStatus = useBookabilityStatus(experience.schedules);

    const handleClick = () => {
        history.push(`/products/experiences/${experience.id}/schedules`);
    };

    return (
        <div className="group flex cursor-pointer rounded p-2" data-testid="experience-tile" onClick={handleClick}>
            <ProductImage product={experience} size="huge" />
            <div className="w-full">
                <div className="min-h-8 flex justify-between">
                    <NoTranslate className="text-lg font-bold line-clamp-2">{experience.name}</NoTranslate>
                    <div className="flex min-w-24 justify-end space-x-2">
                        <SecondaryButton
                            size="small"
                            className="invisible group-hover:visible"
                            icon={<EditIcon />}
                            data-testid="experience-tile-edit-button"
                        >
                            Edit
                        </SecondaryButton>
                        <ExperienceActionButton experience={experience} />
                    </div>
                </div>

                <Tooltip
                    disabled={!isTruncated}
                    content={<NoTranslate data-testid="experience-tile-tooltip-desc">{experience.excerpt}</NoTranslate>}
                >
                    <div ref={excerptRef}>
                        <NoTranslate className="pt-1 text-base line-clamp-2" data-testid="experience-tile-desc">
                            {experience.excerpt}
                        </NoTranslate>
                    </div>
                </Tooltip>

                <div className="mt-2 flex space-x-2">
                    {isVirtual && <ProductBadge text="Online Experience" size="medium" icon={<WifiIcon />} />}
                    {hasPrivateOption(experience) && (
                        <ProductBadge text="Private" color="warning" size="medium" icon={<LockIcon />} />
                    )}
                    {isHidden && <ProductBadge text="Hidden" color="secondary" size="medium" icon={<EyeIcon />} />}
                    {isDraftExperience && (
                        <FadeIn>
                            <Tooltip
                                disabled={draftReasons.length === 0}
                                role="tooltip"
                                placement="bottom"
                                content={<ReasonList reasons={draftReasons} />}
                            >
                                <ProductBadge text="Incomplete" color="danger" size="medium" icon={<WarningIcon />} />
                            </Tooltip>
                        </FadeIn>
                    )}
                    {bookabilityStatus !== BookabilityStatus.BOOKABLE && (
                        <ProductBadge
                            size="medium"
                            color="danger"
                            text={
                                bookabilityStatus === BookabilityStatus.EXPIRED ? "Schedules Expired" : "Full Blackout"
                            }
                            icon={<WarningIcon />}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

interface ProductBadgeProps {
    color?: string;
    text: string | ReactElement;
    icon: ReactElement;
    size: string;
}

const ProductBadge = ({ color = "secondary", text, icon, size }: ProductBadgeProps) => {
    return (
        <Badge color={color} size={size} icon={icon}>
            {text}
        </Badge>
    );
};

const ExperienceActionButton = ({ experience }: { experience: Experience }) => {
    const user = useUser();
    const seller = useSeller();
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [visible, setVisible] = useState(false);
    const invalidate = useInvalidate();
    const isExperienceVisible = experience.visible ?? true;
    const [isEmailPreviewVisible, { toggle: toggleEmailPreview }] = useVisibility(false);
    const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
    const [isDuplicateSuccessModalOpen, setIsDuplicateSuccessModalOpen] = useState(false);
    const [duplicateItems, setDuplicateItems] = useState<DuplicateSuccessData>();
    const isExperiencePublished = experience.status === ExperienceStatus.PUBLISHED;

    const canDuplicateListing = [UserRole.ADMIN, DelegateRole.SELLER_ADMIN, DelegateRole.CURATOR].some((role) =>
        user.roles.includes(role),
    );

    const show = (e: any) => {
        e.stopPropagation();
        setVisible(true);
    };

    const hide = (e: any) => {
        if ("hide" in e) {
            e.hide();
        } else {
            e.stopPropagation();
        }

        setVisible(false);
    };

    const { mutateAsync: deleteExperience, isLoading: isDeleting } = useMutation(async (id: ID) => {
        return api.delete(`/experiences/${id}`);
    });

    const { mutateAsync: updateExperience } = useMutation(async (payload: Partial<Experience>) => {
        return api.put(`/experiences/${payload.id}`, sanitizeExperiencePayload(payload));
    });

    const handleToggleDeleteModal = async (e: any) => {
        e.stopPropagation();
        setIsDeleteModalOpen(!isDeleteModalOpen);
    };

    const invalidateProduct = (id: ID) => {
        invalidate("products", id);
        invalidate("experiences", id);
        invalidate("giftOffers");
    };

    const handleDeleteConfirm = async () => {
        if (experience?.id) {
            await deleteExperience(experience.id);
            invalidateProduct(experience.id);
            flash.success("Experience deleted");
        }

        setIsDeleteModalOpen(false);
    };

    const handleToggleVisibility = async (event: any, value: boolean) => {
        event.stopPropagation();

        const payload: Partial<Experience> = {
            visible: value,
            id: experience.id,
        };

        await updateExperience(payload);
        invalidateProduct(experience.id);
        setVisible(false);
        flash.success("Experience updated");
    };

    const handlePreviewEmail = (e: any) => {
        e.stopPropagation();
        toggleEmailPreview();
        setVisible(false);
    };

    const handleCheckout = (e: any) => {
        e.stopPropagation();
        const url = getButtonUrl(seller, "", ButtonType.SINGLE, experience);
        if (url) {
            window.open(url, "_blank");
        }

        setVisible(false);
    };

    const handleX2Checkout = (e: any) => {
        e.stopPropagation();
        const url = getButtonUrl(seller, "", ButtonType.SINGLE + "_x2", experience);
        setVisible(false);
        openCheckoutIframe(url);
    };

    const handleDuplicate = (e: any) => {
        e.stopPropagation();
        setIsDuplicateModalOpen(true);
    };

    const handleDuplicateExperienceModalClose = (isConfirmed: boolean, data?: DuplicateSuccessData) => {
        setIsDuplicateModalOpen(false);
        setDuplicateItems(data);
        setIsDuplicateSuccessModalOpen(isConfirmed);
    };

    const handleDuplicateSuccessModalClose = (redirectCallback: () => void) => {
        setIsDuplicateSuccessModalOpen(false);
        redirectCallback?.();
    };

    const showX2Checkout = isX2CheckoutSupported({ type: ButtonType.SINGLE }, experience);

    return (
        <>
            <PopoverList visible={visible} className="w-50 items-center" zIndex="10" onClickOutside={hide}>
                <SecondaryButton
                    size="small"
                    data-testid="experience-action-button-ellipsis"
                    onClick={visible ? hide : show}
                >
                    <EllipsisIcon />
                </SecondaryButton>
                {isExperiencePublished && (
                    <PopoverList.Item name="Preview Email" onClickItem={handlePreviewEmail}>
                        <EmailIcon />
                        <span>Preview Email</span>
                    </PopoverList.Item>
                )}
                {isExperiencePublished && (isXolaAdmin(user) || !showX2Checkout) && (
                    <PopoverList.Item name="Preview Checkout" onClickItem={handleCheckout}>
                        <ShoppingBagIcon className="top-0" />
                        <span>Preview Checkout</span>
                    </PopoverList.Item>
                )}
                {isExperiencePublished && showX2Checkout && (
                    <PopoverList.Item name="Preview X2 Checkout" onClickItem={handleX2Checkout}>
                        <CartIcon className="top-0" />
                        <span>Preview {isXolaAdmin(user) ? "X2 " : ""}Checkout</span>
                    </PopoverList.Item>
                )}

                {canDuplicateListing && (
                    <PopoverList.Item name="Duplicate" role="button" onClickItem={handleDuplicate}>
                        <DuplicateIcon />
                        <span>Duplicate</span>
                    </PopoverList.Item>
                )}
                <PopoverList.Item
                    name="Visibility"
                    role="button"
                    onClickItem={async (e: any) => handleToggleVisibility(e, !isExperienceVisible)}
                >
                    <EyeIcon />
                    <span>{isExperienceVisible ? "Hide" : "Unhide"}</span>
                </PopoverList.Item>
                <PopoverList.Item name="Delete" role="button" onClickItem={handleToggleDeleteModal}>
                    <TrashIcon />
                    <span>Delete</span>
                </PopoverList.Item>
            </PopoverList>
            <PreviewMessageTemplate
                isOpen={isEmailPreviewVisible}
                payload={{
                    template: EmailTemplate.PURCHASE_CONFIRMATION,
                    experience: experience.id,
                }}
                onClose={() => toggleEmailPreview()}
            />
            <PromptModal
                title="Delete Experience?"
                isLoading={isDeleting}
                isOpen={isDeleteModalOpen}
                onConfirm={handleDeleteConfirm}
                onClose={handleToggleDeleteModal}
            >
                Are you sure you want to delete this experience?
            </PromptModal>
            <DuplicateExperienceModal
                isOpen={isDuplicateModalOpen}
                originalExperience={experience}
                onClose={handleDuplicateExperienceModalClose}
            />
            <DuplicateSuccessModal
                isOpen={isDuplicateSuccessModalOpen}
                data={duplicateItems}
                onClose={handleDuplicateSuccessModalClose}
            />
        </>
    );
};

const ReasonList = ({ reasons }: { reasons: string[] }) => {
    return (
        <ul className="ml-3 list-disc space-y-2 p-2">
            {reasons.map((reason) => (
                <li key={reason}>{reason}</li>
            ))}
        </ul>
    );
};
